import { memo, useCallback, useState } from "react";
import { useTranslate } from "helpers";
import { downloadFile } from "helpers/common";
import {
  importFleetVehiclesAPI,
  getFleetVehiclesImportTemplateAPI,
} from "services/api/fleet/vehicles/api";
import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import { Wizard } from "ui/components/Wizard";
import WizardStep from "ui/components/Wizard/WizardStep";
import WizardFooter from "ui/components/Wizard/WizardFooter";
import { Loader } from "ui/components/Loader";
import { FileExtended } from "ui/components/FileUpload/FileDropZone/types";
import { VehicleImportModalProps } from "./types";
import VehicleImportFileModal from "./components/VehicleImportFileModal";
import ImportTemplateStep from "./components/ImportTemplateStep/ImportTemplateStep";
import ImportFileStep from "./components/ImportFileStep/ImportFileStep";
import ImportIntro from "./components/ImportIntro/ImportIntro";
import { validateYup } from "services/forms/validation/validation";
import { Errors } from "services/forms/validation/types";
import { FLEET_VEHICLE_IMPORT_FORM_SCHEMA } from "./validation";
import { FleetVehicleImportModalFormValues } from "services/api/fleet/vehicles/types";
import { initialVehicleImportModalForm } from "services/api/fleet/vehicles/consts";
import { showNotification } from "services/api/snackbar";

const VehicleImportModal: React.FC<VehicleImportModalProps> = (props) => {
  const { show, onClose, onImportSuccess } = props;

  const { t } = useTranslate();
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
  const [formValues, setFormValues] = useState<FleetVehicleImportModalFormValues>(initialVehicleImportModalForm);
  const [errors, setErrors] = useState<Errors>({});
  const [showFileModal, setShowFileModal] = useState(false);
  const [stepCurrent, setStepCurrent] = useState(0);

  const selectedFile = formValues.files[0];

  const handleOpenFileModal = useCallback(() => setShowFileModal(true), []);
  const handleCloseFileModal = useCallback(() => setShowFileModal(false), []);

  const handleFileSelect = useCallback((files: FileExtended[]) => {
    setFormValues((prev) => ({ ...prev, files }));
    setErrors({});
  }, []);

  const handleRemoveFile = useCallback(() => {
    setFormValues((prev) => ({ ...prev, files: [] }));
  }, []);

  const handleDownloadTemplate = useCallback(async () => {
    setIsDownloadingTemplate(true);
    try {
      const response = await getFleetVehiclesImportTemplateAPI();
      if (response) {
        downloadFile(response, "xlsx", "FLEET_VEHICLE.xlsx");
      }
    } catch (error) {
      console.error("Template download failed:", error);
    } finally {
      setIsDownloadingTemplate(false);
    }
  }, []);

  const handleNextStep = useCallback(() => {
    setStepCurrent((prev) => prev + 1);
  }, []);

  const handleBackStep = useCallback(() => {
    setStepCurrent((prev) => prev - 1);
  }, []);

  const handleClose = useCallback(() => {
    setFormValues((prev) => ({ ...prev, files: [] }));
    setErrors({});
    setShowFileModal(false);
    setStepCurrent(0);
    onClose();
  }, [onClose]);

  const handleFinish = useCallback(async () => {
    const isValid = validateYup(formValues, FLEET_VEHICLE_IMPORT_FORM_SCHEMA, setErrors);
    if (!isValid) {
      return;
    }

    setIsLoading(true);
    try {
      const result = await importFleetVehiclesAPI(formValues.files[0]);

      if (result.successCount > 0) {
        onImportSuccess();
      }

      showNotification(
        result.errors.length > 0
          ? `Uvezeno ${result.successCount} vozila, ${result.errors.length} preskočeno.`
          : `Uspješno uvezeno ${result.successCount} vozila.`,
        { variant: result.errors.length > 0 ? "warning" : "success" }
      );

      handleClose();
    } catch (error) {
      console.error("Import failed:", error);
    } finally {
      setIsLoading(false);
    }
  }, [formValues, onImportSuccess, handleClose]);

  const steps = [
    <WizardStep key={0} stepNumber={0} stepCurrent={stepCurrent}>
      <ImportTemplateStep
        isDownloadingTemplate={isDownloadingTemplate}
        onDownloadTemplate={handleDownloadTemplate}
      />
    </WizardStep>,

    <WizardStep key={1} stepNumber={1} stepCurrent={stepCurrent}>
      <ImportFileStep
        selectedFile={selectedFile}
        error={errors["files"]}
        onOpenFileModal={handleOpenFileModal}
        onRemoveFile={handleRemoveFile}
      />
    </WizardStep>,
  ];

  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        size="full"
        position="side"
        intro={{
          content: (
            <ImportIntro
              stepCurrent={stepCurrent}
              stepCount={steps.length}
              fileName={selectedFile?.name ?? null}
            />
          ),
        }}
      >
        <ModalHeader
          icon="/media/images/add-icon.svg"
          title={t("admin.fleet.vehicle.import.xlsx.title")}
          onHide={handleClose}
        />
        <ModalBody>
          <Wizard steps={steps} />
        </ModalBody>

        <Loader
          isLoading={isDownloadingTemplate || isLoading}
          overlay
          message={
            isDownloadingTemplate
              ? "Preuzimanje predloška..."
              : "Uvoz vozila u tijeku..."
          }
          className="fixed inset-y-0 right-0 w-1/2"
        />

        <WizardFooter
          stepCurrent={stepCurrent}
          steps={steps}
          onNextStep={handleNextStep}
          onBackStep={handleBackStep}
          onFinish={handleFinish}
          isLoading={isLoading}
          isReadOnly={false}
        />
      </Modal>

      <VehicleImportFileModal
        show={showFileModal}
        onClose={handleCloseFileModal}
        onSelect={handleFileSelect}
      />
    </>
  );
};

export default memo(VehicleImportModal);
